<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MessageList from './MessageList.vue';
import CopilotThreadsAPI from 'dashboard/api/captain/copilotThreads';
import CopilotMessagesAPI from 'dashboard/api/captain/copilotMessages';

const { assistantId } = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();
const messages = ref([]);
const newMessage = ref('');
const isLoading = ref(false);
const threadId = ref(null);

const STORAGE_KEY = 'airjump:playground_thread';

// Load playground thread from localStorage + backend
onMounted(async () => {
  const savedId = localStorage.getItem(STORAGE_KEY);
  if (savedId) {
    threadId.value = Number(savedId);
    await loadMessages(Number(savedId));
  }
});

const loadMessages = async id => {
  try {
    const { data } = await CopilotMessagesAPI.get(id);
    const records = data.payload || data;
    if (Array.isArray(records)) {
      messages.value = records
        .filter(m => m.message_type === 'user' || m.message_type === 'assistant')
        .map(m => ({
          content: m.message?.content || '',
          sender: m.message_type,
          timestamp: m.created_at,
        }));
    }
  } catch {}
};

const resetConversation = async () => {
  messages.value = [];
  newMessage.value = '';
  threadId.value = null;
  localStorage.removeItem(STORAGE_KEY);
};

const sendMessage = async () => {
  if (!newMessage.value.trim() || isLoading.value) return;

  const userMessage = {
    content: newMessage.value,
    sender: 'user',
    timestamp: new Date().toISOString(),
  };
  messages.value.push(userMessage);
  const currentMessage = newMessage.value;
  newMessage.value = '';

  try {
    isLoading.value = true;

    if (threadId.value) {
      // Continue existing thread
      const { data } = await CopilotMessagesAPI.create({
        threadId: threadId.value,
        message: currentMessage,
        assistant_id: assistantId,
      });
      // The response includes the assistant message via ActionCable,
      // but we can also poll or wait
      waitForAssistantReply();
    } else {
      // Create new playground thread
      const { data } = await CopilotThreadsAPI.create({
        assistant_id: assistantId,
        message: currentMessage,
      });
      const thread = data.payload || data;
      threadId.value = thread.id;
      localStorage.setItem(STORAGE_KEY, thread.id);
      waitForAssistantReply();
    }
  } catch (error) {
    console.error('Error sending message:', error);
    isLoading.value = false;
  }
};

// Poll for assistant reply (ActionCable handles real-time, but as fallback)
const waitForAssistantReply = () => {
  let attempts = 0;
  const poll = setInterval(async () => {
    attempts++;
    if (attempts > 30) {
      clearInterval(poll);
      isLoading.value = false;
      return;
    }
    try {
      const { data } = await CopilotMessagesAPI.get(threadId.value);
      const records = data.payload || data;
      if (Array.isArray(records)) {
        const assistantMsgs = records.filter(m => m.message_type === 'assistant');
        const lastAssistant = assistantMsgs[assistantMsgs.length - 1];
        const currentAssistantCount = messages.value.filter(m => m.sender === 'assistant').length;
        if (assistantMsgs.length > currentAssistantCount && lastAssistant?.message?.content) {
          messages.value.push({
            content: lastAssistant.message.content,
            sender: 'assistant',
            timestamp: lastAssistant.created_at,
          });
          clearInterval(poll);
          isLoading.value = false;
        }
      }
    } catch {}
  }, 2000);
};

const handleEnterKey = event => {
  if (event.isComposing) return;
  event.preventDefault();
  sendMessage();
};
</script>

<template>
  <div
    class="flex flex-col h-full rounded-xl border py-6 border-n-weak text-n-slate-11"
  >
    <div class="mb-8 px-6">
      <div class="flex justify-between items-center mb-1">
        <h3 class="text-lg font-medium">
          {{ t('CAPTAIN.PLAYGROUND.HEADER') }}
        </h3>
        <NextButton
          ghost
          sm
          slate
          icon="i-lucide-rotate-ccw"
          @click="resetConversation"
        />
      </div>
      <p class="text-sm text-n-slate-11">
        {{ t('CAPTAIN.PLAYGROUND.DESCRIPTION') }}
      </p>
    </div>

    <MessageList :messages="messages" :is-loading="isLoading" />

    <div
      class="flex items-center mx-6 bg-n-background outline outline-1 outline-n-weak rounded-xl p-3"
    >
      <input
        v-model="newMessage"
        class="flex-1 bg-transparent border-none focus:outline-none text-sm mb-0 text-n-slate-12 placeholder:text-n-slate-10"
        :placeholder="t('CAPTAIN.PLAYGROUND.MESSAGE_PLACEHOLDER')"
        @keydown.enter.exact="handleEnterKey"
      />
      <NextButton
        ghost
        sm
        :disabled="!newMessage.trim()"
        icon="i-lucide-send"
        @click="sendMessage"
      />
    </div>
  </div>
</template>
